import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ChevronLeft, ChevronRight, Calendar, MapPin, ArrowLeft, Bookmark } from 'lucide-react';
import { useChampionship } from '@/contexts/ChampionshipContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import volleyballApi from '@/services/api';

export const MatchesByDatePage = () => {
  const navigate = useNavigate();
  const { favorites } = useChampionship();
  const [championships, setChampionships] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Generate week days around selected date
  const weekDays = useMemo(() => {
    const days = [];
    const startOfWeek = new Date(selectedDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1); // Start from Monday
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  }, [selectedDate]);

  // Day names in Italian
  const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  useEffect(() => {
    loadData();
  }, [favorites]);

  const loadData = async () => {
    if (favorites.length === 0) {
      console.log('⚠️ [Calendar] No favorites, skipping load');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 [Calendar] Loading data for favorites:', favorites);
      
      // Load championships info
      const champsData = await volleyballApi.getChampionships();
      console.log('✅ [Calendar] Championships API response:', champsData);
      
      // Handle new format: {championships: [...], categoryOrder: [...]}
      const champsList = champsData.championships || champsData;
      console.log('📦 [Calendar] Championships list:', champsList.length);
      
      const favoriteChamps = champsList.filter(c => favorites.includes(c.id));
      console.log('⭐ [Calendar] Favorite championships:', favoriteChamps.length);
      setChampionships(favoriteChamps);

      // Load matches for each favorite championship
      console.log('🔄 [Calendar] Loading matches for', favorites.length, 'championships');
      const matchesPromises = favorites.map(async (champId) => {
        try {
          const matches = await volleyballApi.getChampionshipMatches(champId);
          console.log(`✅ [Calendar] Loaded ${matches.length} matches for championship ${champId}`);
          return matches.map(m => ({ ...m, championshipId: champId }));
        } catch (error) {
          console.error(`❌ [Calendar] Error loading matches for ${champId}:`, error);
          return [];
        }
      });

      const matchesArrays = await Promise.all(matchesPromises);
      const allMatchesData = matchesArrays.flat();
      console.log('✅ [Calendar] Total matches loaded:', allMatchesData.length);
      setAllMatches(allMatchesData);
    } catch (error) {
      console.error('❌ [Calendar] Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract date from match date string (handles both "DD/MM/YYYY" and "Dom DD/MM/YYYY" formats)
  function extractDate(dateStr) {
    if (!dateStr) return null;
    const dateMatch = dateStr.match(/(\d{2}\/\d{2}\/\d{4})/);
    return dateMatch ? dateMatch[1] : dateStr;
  }

  // Filter matches by selected date
  const matchesForSelectedDate = useMemo(() => {
    const selectedDateStr = formatDateForComparison(selectedDate);
    
    return allMatches.filter(match => {
      if (!match.date) return false;
      const matchDateStr = extractDate(match.date);
      return matchDateStr === selectedDateStr;
    });
  }, [allMatches, selectedDate]);

  // Group matches by championship
  const matchesByChampionship = useMemo(() => {
    const grouped = {};
    matchesForSelectedDate.forEach(match => {
      if (!grouped[match.championshipId]) {
        grouped[match.championshipId] = [];
      }
      grouped[match.championshipId].push(match);
    });
    return grouped;
  }, [matchesForSelectedDate]);

  function formatDateForComparison(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function formatDateHeader(date) {
    const day = date.getDate();
    const months = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  function isToday(date) {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  function isSameDay(date1, date2) {
    return date1.toDateString() === date2.toDateString();
  }

  const goToPreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const getChampionshipInfo = (champId) => {
    return championships.find(c => c.id === champId) || { name: 'Campionato', fullName: 'Campionato' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento partite...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Partite
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Date Selector */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-3 py-3">
          {/* Current Date Display */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={goToPreviousWeek}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="bg-gray-900 dark:bg-gray-700 text-white px-3 py-1.5 rounded-lg font-medium text-sm">
              {formatDateHeader(selectedDate)}
            </div>
            <button
              onClick={goToNextWeek}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
            >
              <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Week Days - Fixed width for mobile */}
          <div className="flex justify-between gap-1 overflow-x-auto">
            {weekDays.map((day, index) => {
              const isSelected = isSameDay(day, selectedDate);
              const dayIsToday = isToday(day);
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(day)}
                  className={`flex flex-col items-center py-1.5 px-2 rounded-lg transition-all flex-1 min-w-0 ${
                    isSelected
                      ? 'bg-yellow-400 dark:bg-yellow-500 text-gray-900'
                      : dayIsToday
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  <span className="text-[10px] font-medium">
                    {dayNames[index]}
                  </span>
                  <span className={`text-sm font-bold ${isSelected ? 'text-gray-900' : ''}`}>
                    {day.getDate()}.{String(day.getMonth() + 1).padStart(2, '0')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-4 max-w-2xl">
        {favorites.length === 0 ? (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-center">
            <Bookmark className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Nessun campionato nei preferiti
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Aggiungi campionati ai preferiti per vedere le partite qui.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Vai ai Campionati
            </button>
          </div>
        ) : matchesForSelectedDate.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Nessuna partita in questa data
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Non ci sono partite programmate per il {formatDateHeader(selectedDate)} nei campionati preferiti.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(matchesByChampionship).map(([champId, matches]) => {
              const champInfo = getChampionshipInfo(champId);
              
              return (
                <div key={champId}>
                  {/* Championship Header */}
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Trophy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {champInfo.fullName || champInfo.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        C.T. BARI FOGGIA
                      </p>
                    </div>
                  </div>

                  {/* Matches */}
                  {matches.map((match) => (
                    <div
                      key={match.id}
                      onClick={() => navigate(`/championship/${champId}/partita/${match.id}`)}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 mb-3 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between gap-2">
                        {/* Home Team */}
                        <div className="flex-1 text-center min-w-0 overflow-hidden">
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-1">
                            <Trophy className="h-5 w-5 text-gray-400" />
                          </div>
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate px-1">
                            {match.homeTeam}
                          </p>
                        </div>

                        {/* Score or Time */}
                        <div className="flex-shrink-0">
                          {match.played ? (
                            <div className="bg-green-100 dark:bg-green-900/30 px-2 py-1.5 rounded-lg">
                              <span className="text-sm font-bold text-green-700 dark:text-green-400 whitespace-nowrap">
                                {match.homeScore} - {match.awayScore}
                              </span>
                            </div>
                          ) : (
                            <div className="bg-yellow-400 dark:bg-yellow-500 px-2 py-1.5 rounded-lg">
                              <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
                                {match.time || '--:--'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Away Team */}
                        <div className="flex-1 text-center min-w-0 overflow-hidden">
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-1">
                            <Trophy className="h-5 w-5 text-gray-400" />
                          </div>
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate px-1">
                            {match.awayTeam}
                          </p>
                        </div>
                      </div>

                      {/* Venue */}
                      {match.venue && (
                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate max-w-full">{match.venue}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Banner */}
        {favorites.length > 0 && (
          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
              <Bookmark className="inline h-4 w-4 mr-1 text-blue-500" />
              Stai seguendo <strong>{favorites.length}</strong> campionat{favorites.length === 1 ? 'o' : 'i'}.
            </p>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <div className="flex justify-around py-2">
            <button
              onClick={() => navigate('/')}
              className="flex flex-col items-center py-2 px-4 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            >
              <Trophy className="h-6 w-6" />
              <span className="text-xs mt-1">Campionati</span>
            </button>
            <button
              className="flex flex-col items-center py-2 px-4 text-blue-600 dark:text-blue-400"
            >
              <Calendar className="h-6 w-6" />
              <span className="text-xs mt-1 font-medium">Partite</span>
            </button>
            <button
              onClick={() => navigate('/preferiti')}
              className="flex flex-col items-center py-2 px-4 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 relative"
            >
              <Bookmark className="h-6 w-6" />
              <span className="text-xs mt-1">Preferiti</span>
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {favorites.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
